/**
 * `tuninho:chart` — o registro, em UM lugar.
 *
 * Três pontos precisam conhecer o bloco, e cada um responde a uma pergunta diferente:
 *
 * | Onde | Pergunta |
 * |---|---|
 * | `manager/store.ts` | o modelo hidrata a partir do Y.Doc? |
 * | `manager/view.ts` | há elemento para desenhá-lo? |
 * | `modules/workspace/global-schema.ts` | o `ZipTransformer` sabe importá-lo? |
 * | `configs/slash-menu.ts` (aqui, no `setup`) | **há como INSERIR um?** |
 *
 * ⚠️ Os dois primeiros são o par que sempre se lembra. O **terceiro** é o que se esquece, e o
 * modo de falhar dele é o pior: o documento abre e funciona no editor, e a IMPORTAÇÃO de um
 * `.zip` que contenha o bloco o descarta em silêncio. Só apareceria no dia em que alguém
 * exportasse e reimportasse um documento com gráfico — e o gráfico simplesmente não estaria
 * mais lá.
 *
 * Este arquivo existe para que os três sejam UMA importação cada (Regra #9), e o
 * `test_bloco_de_grafico.py` confere que os três estão ligados.
 */

import type { ElementOrFactory } from '@affine/component';
import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine/ext-loader';
import { BlockViewExtension } from '@blocksuite/affine/std';
import type { ExtensionType } from '@blocksuite/affine/store';
import { SlashMenuConfigExtension } from '@blocksuite/affine/widgets/slash-menu';
import type { TemplateResult } from 'lit';
import { literal } from 'lit/static-html.js';
import { z } from 'zod';

import { TuninhoChartBlockComponent } from './bloco';
import { graficoSlashMenuConfig } from './configs/slash-menu';
import { FLAVOUR_DO_GRAFICO } from './model';
import { renderizarGraficoDoModelo } from './ponte-react';

export * from './dados';
export * from './model';
export { TuninhoChartBlockComponent } from './bloco';
export { Grafico, type PropsDoGrafico } from './grafico';

const opcoesSchema = z.object({
  reactToLit: z.optional(
    z
      .function()
      .args(z.custom<ElementOrFactory>(), z.boolean().optional())
      .returns(z.custom<TemplateResult>())
  ),
});

export type OpcoesDoGrafico = z.infer<typeof opcoesSchema>;

export const TuninhoChartBlockSpec: ExtensionType[] = [
  BlockViewExtension(FLAVOUR_DO_GRAFICO, literal`tuninho-chart`),
  /*
   * ⚠️ **Desenhar e INSERIR são registros diferentes, e faltar o segundo não falha — some.**
   *
   * Até 2026-08-28 esta lista tinha só o `BlockViewExtension`. O bloco desenhava quando já
   * estava no documento (é o que o Space demonstrativo prova) e **não havia como criar um**:
   * medido no `dev`, o menu do `/` abria com 55 itens e nenhum era o gráfico.
   *
   * É a oitava ocorrência da família 8.1 / 8.4 / 8.18.1 / 8.26 / 8.32 / 8.46.1 / 8.52 — e a
   * terceira cometida por este mesmo bloco. A pergunta que o teste passa a fazer não é "o
   * arquivo é importado?" (importar não é registrar — Regra 8.52) e sim "o menu do `/`
   * oferece o gráfico?".
   */
  SlashMenuConfigExtension(FLAVOUR_DO_GRAFICO, graficoSlashMenuConfig),
];

export class TuninhoChartViewExtension extends ViewExtensionProvider<OpcoesDoGrafico> {
  override name = 'tuninho-view-chart';

  override schema = opcoesSchema;

  /**
   * ⚠️ **`effect()` é onde o custom element nasce, e ESQUECÊ-LO não falha — some.**
   *
   * O `ViewExtensionProvider` tem dois ganchos, e eles respondem a coisas diferentes:
   * `setup()` diz ao editor *qual tag desenha este flavour*; `effect()` é o que **define a
   * tag no navegador**. Sobrescrevendo só o primeiro, o BlockSuite encontrava o bloco, criava
   * `<tuninho-chart>` no DOM — e o navegador o tratava como elemento desconhecido: **0×0, sem
   * filhos, sem shadowRoot, sem um único erro de console**.
   *
   * Medido no `dev` em 2026-08-26: o elemento estava lá, com o `data-block-id` certo, e o
   * gráfico nunca havia renderizado em ambiente nenhum. A função que o registra existia desde
   * o primeiro commit e **não tinha chamador** — a família das regras 8.1, 8.4, 8.18.1, 8.26,
   * 8.32 e 8.46.1 pela sétima vez, desta vez cometida por este mesmo card.
   *
   * O idioma é o do upstream (`blocks/paragraph/src/view.ts:43`): `override effect()` chamando
   * `super.effect()` e depois o `customElements.define`.
   */
  override effect(): void {
    super.effect();
    registrarElementoDoGrafico();
  }

  override setup(context: ViewExtensionContext, options?: OpcoesDoGrafico) {
    super.setup(context, options);

    // Sem a ponte React não há o que desenhar — mas o bloco AINDA é registrado, porque
    // registrar o elemento é o que faz o documento carregar o bloco em vez de descartá-lo.
    // O componente mostra o aviso; o dado não se perde. Não registrar aqui seria escolher a
    // perda silenciosa em vez do aviso.
    context.register(TuninhoChartBlockSpec);

    if (options?.reactToLit) {
      /*
       * ⚠️ **Os DOIS campos, ou nenhum.** O `renderBlock` exige `reactParaLit` **e**
       * `renderizarGrafico`; faltando qualquer um, ele cai no aviso *"Este gráfico não pode ser
       * desenhado aqui"*. A versão anterior preenchia só o primeiro — e o segundo não tinha
       * escritor em lugar nenhum do repositório, então o aviso era o único caminho que o
       * código percorria, em toda instalação.
       */
      TuninhoChartBlockComponent.prototype.reactParaLit = options.reactToLit;
      TuninhoChartBlockComponent.prototype.renderizarGrafico = renderizarGraficoDoModelo;
    }
  }
}

/** O custom element precisa existir antes de o Lit encontrar a tag. */
export function registrarElementoDoGrafico() {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('tuninho-chart')) return;
  customElements.define('tuninho-chart', TuninhoChartBlockComponent);
}
